/**
 * Hosts that are not plainly on the public internet: this machine, the
 * user's network, local names and every reserved address range. The
 * desktop webview has no CSP, so a fetch driven by frame content (a PDF's
 * URL) must not reach them.
 *
 * `hostname` is `URL.hostname`: lowercased, IPv4 normalised to four decimal
 * parts (integer, octal and hex forms included), IPv6 in brackets and
 * compressed to hex groups.
 */

const LOCAL_NAMES = [
  "localhost",
  ".localhost",
  ".local",
  ".internal",
  ".lan",
  ".home.arpa",
];

/** Reserved IPv4 ranges as [network, prefix length]. */
const RESERVED_IPV4: ReadonlyArray<readonly [string, number]> = [
  ["0.0.0.0", 8], // "this network"
  ["10.0.0.0", 8], // private
  ["100.64.0.0", 10], // carrier-grade NAT
  ["127.0.0.0", 8], // loopback
  ["169.254.0.0", 16], // link-local
  ["172.16.0.0", 12], // private
  ["192.0.0.0", 24], // IETF protocol assignments
  ["192.0.2.0", 24], // documentation
  ["192.168.0.0", 16], // private
  ["198.18.0.0", 15], // benchmarking
  ["198.51.100.0", 24], // documentation
  ["203.0.113.0", 24], // documentation
  ["224.0.0.0", 4], // multicast
  ["240.0.0.0", 4], // reserved and broadcast
];

function ipv4Value(address: string): number | null {
  const parts = address.split(".");
  if (parts.length !== 4 || !parts.every((part) => /^\d{1,3}$/.test(part))) {
    return null;
  }
  const bytes = parts.map(Number);
  if (bytes.some((byte) => byte > 255)) return null;
  return bytes.reduce((value, byte) => value * 256 + byte, 0);
}

function reservedIpv4Value(value: number): boolean {
  return RESERVED_IPV4.some(([network, bits]) => {
    const size = 2 ** (32 - bits);
    const start = ipv4Value(network)!;
    return value >= start && value < start + size;
  });
}

/**
 * The eight 16-bit groups of an IPv6 address as `URL` serialises it (hex
 * groups, `::` compression, never a dotted tail), or null: anything else
 * fails closed as private.
 */
function ipv6Groups(address: string): number[] | null {
  const halves = address.split("::");
  if (halves.length > 2) return null;
  const parse = (half: string) =>
    half === ""
      ? []
      : half
          .split(":")
          .map((group) =>
            /^[0-9a-f]{1,4}$/.test(group) ? parseInt(group, 16) : NaN,
          );
  const head = parse(halves[0]!);
  const tail = halves.length === 2 ? parse(halves[1]!) : [];
  const known = head.length + tail.length;
  if (halves.length === 1 ? known !== 8 : known > 7) return null;
  const groups = [...head, ...Array<number>(8 - known).fill(0), ...tail];
  return groups.every(Number.isFinite) ? groups : null;
}

/** The IPv4 address in groups `at` and `at + 1`. */
function embeddedIpv4(groups: readonly number[], at: number): number {
  return groups[at]! * 65536 + groups[at + 1]!;
}

/**
 * Public only within global unicast (2000::/3), minus documentation
 * (2001:db8::/32), Teredo (2001::/32) and 6to4 (2002::/16) over a reserved
 * IPv4; NAT64 (64:ff9b::/96) counts as its embedded IPv4. Everything else
 * (loopback, unspecified, IPv4-mapped and -compatible, unique local,
 * link-local, multicast) is private.
 */
function privateIpv6(address: string): boolean {
  const groups = ipv6Groups(address);
  if (!groups) return true;
  const [first, second] = groups as [number, number];
  if (
    first === 0x64 &&
    second === 0xff9b &&
    groups.slice(2, 6).every((group) => group === 0)
  ) {
    return reservedIpv4Value(embeddedIpv4(groups, 6));
  }
  if ((first & 0xe000) !== 0x2000) return true;
  if (first === 0x2001 && (second === 0x0db8 || second === 0)) return true;
  if (first === 0x2002) return reservedIpv4Value(embeddedIpv4(groups, 1));
  return false;
}

export function isPrivateHost(hostname: string): boolean {
  const name = hostname.replace(/\.$/, "");
  if (name.startsWith("[")) return privateIpv6(name.slice(1, -1));
  const ipv4 = ipv4Value(name);
  if (ipv4 !== null) return reservedIpv4Value(ipv4);
  return (
    !name.includes(".") ||
    LOCAL_NAMES.some((local) =>
      local.startsWith(".") ? name.endsWith(local) : name === local,
    )
  );
}
